import axios, { type AxiosError, type AxiosInstance, type AxiosResponse } from 'axios';
import { RustyApiKeys, type RustyApiKeysSpec } from './api-keys';
import type { RustyAuthSpec } from './auth';
import {
  type ReauthPurpose,
  RustyConnectedAccounts,
  type RustyConnectedAccountsSpec,
  type StepUpChallenge,
  type UserIdentitySummary,
} from './connected-accounts';
import { RustyDeposit, type RustyDepositSpec } from './deposit';
import type {
  DeliveriesListDto,
  DeliveriesQueryParams,
  FormIntegrationDto,
  FormIntegrationsListDto,
  IntegrationDto,
  IntegrationsListDto,
  IntegrationsListQueryParams,
  LinkFormIntegrationBody,
  NewIntegrationBody,
  RotateSecretResponse,
  UpdateFormIntegrationBody,
  UpdateIntegrationBody,
} from './integrations';
import type { NotificationConfig } from './notifications';
import { RustyReferral, type RustyReferralSpec } from './referral';
import {
  RustySecurityLog,
  type RustySecurityLogSpec,
  type SecurityLogQuery,
  type SecurityLogResponse,
} from './security-log';
import { RustySubscription, type RustySubscriptionSpec } from './subscription';
import { type ApiProps, makeUrl } from './types/api';
import type {
  ApiKeyCreateRequest,
  ApiKeyResponse,
  ApiKeyUpdateRequest,
  ApiKeyUsageResponse,
  CommonQueryParams,
  CustomerPortalResponse,
  Form,
  FormAnalyticsAggregateResponse,
  FormAnalyticsResponse,
  FormRecipientsQueryParams,
  FormsQueryParams,
  FormsRecipientsResponse,
  FormsResponse,
  FormView,
  HttpNewForm,
  HttpNewVerifiedEmail,
  HttpUpdateMessage,
  IssuesQueryParams,
  IssuesResponse,
  MessageCountByDay,
  MessageQueryParams,
  MessagesResponse,
  NewDepositHttp,
  NewFormsRecipient,
  NewSubscriptionResponse,
  RecoveryCodesCount,
  RecoveryCodesResponse,
  ReferralCodeResponse,
  ReferralHistoryItem,
  ReferralStatsResponse,
  SingleMessageResponse,
  SubscriptionPlanConfig,
  SubscriptionResponse,
  TotpSetupResponse,
  TwoFactorSetupComplete,
  TwoFactorStatus,
  UpdateForm,
  UpdateFormSpecs,
  UsageResponse,
  VerifiedEmailsResponse,
  ViewCountByDay,
} from './types/generated';
import type { ListResponse } from './types/query';
import { makeAuthHeaders } from './utils/make-auth-headers';

export class RustyFormsApi {
  private baseUrl: string;
  private timeout: number;
  private client: AxiosInstance;
  private errorHandler?: (error: AxiosError) => void;
  private successHandler?: (response: AxiosResponse) => void;
  auth?: RustyAuthSpec;

  constructor({ baseUrl, timeout, auth, errorHandler, successHandler }: ApiProps) {
    if (baseUrl) {
      this.baseUrl = baseUrl;
    } else {
      this.baseUrl = 'https://api.formshive.com/v1';
    }

    console.info(`[api] => baseUrl: ${this.baseUrl}`);

    if (timeout) {
      this.timeout = timeout;
    } else {
      this.timeout = 5000;
    }

    if (auth) {
      this.auth = auth;
    } else {
      console.warn('Auth config not set');
    }

    this.errorHandler = errorHandler;
    this.successHandler = successHandler;

    this.client = axios.create({
      baseURL: this.baseUrl,
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to automatically handle authentication
    this.client.interceptors.request.use(async (config) => {
      // Check if this request needs auth (uses auth endpoints)
      const needsAuth = config.url?.startsWith('/a/');

      if (needsAuth && this.auth) {
        if (!this.auth.hasValidAccessToken()) {
          await new Promise((resolve) => setTimeout(resolve, 500)); // Wait for auth to be ready
        }
        const token = this.auth.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    });

    // Add response interceptor to handle errors / success generically
    this.client.interceptors.response.use(
      (response) => {
        if (this.successHandler) {
          this.successHandler(response);
        }
        return response; // Pass through successful responses
      }, // Pass through successful responses
      (error: AxiosError) => {
        if (this.errorHandler) {
          this.errorHandler(error);
        }
        return Promise.reject(error); // Still reject so specific handlers can override
      }
    );
  }

  getAuthApi = (): RustyAuthSpec => {
    if (!this.auth) {
      throw new Error('Auth config not set');
    }
    return this.auth;
  };

  private getDepositApi = (): RustyDepositSpec => new RustyDeposit({ client: this.client });

  private getSubscriptionApi = (): RustySubscriptionSpec =>
    new RustySubscription({ client: this.client });

  private getReferralApi = (): RustyReferralSpec => new RustyReferral({ client: this.client });

  private getApiKeysApi = (): RustyApiKeysSpec => new RustyApiKeys({ client: this.client });

  private getSecurityLogApi = (): RustySecurityLogSpec =>
    new RustySecurityLog({ client: this.client });

  private getConnectedAccountsApi = (): RustyConnectedAccountsSpec =>
    new RustyConnectedAccounts({ client: this.client });

  private getAccessToken = () => this.getAuthApi().getAccessToken();

  private getAuthHeaders = () => makeAuthHeaders(this.getAccessToken());

  private getAxiosConfig = () => ({ headers: this.getAuthHeaders(), timeout: this.timeout });

  getAccountBalance = async () => this.getDepositApi().getAccountBalance(this.getAccessToken());

  getAccountMovements = async (query: CommonQueryParams = {}) =>
    this.getDepositApi().getAccountMovements(query, this.getAccessToken());

  getDeposits = async (query: CommonQueryParams) =>
    this.getDepositApi().getDeposits(query, this.getAccessToken());

  getDeposit = async (id: string) => this.getDepositApi().getDeposit(id, this.getAccessToken());

  newDeposit = async (deposit: NewDepositHttp) =>
    this.getDepositApi().newDeposit(deposit, this.getAccessToken());

  // Subscription endpoints
  getSubscriptionPlans = async (): Promise<SubscriptionPlanConfig[]> =>
    this.getSubscriptionApi().getSubscriptionPlans();

  getCurrentSubscription = async (): Promise<SubscriptionResponse | null> =>
    this.getSubscriptionApi().getCurrentSubscription(this.getAccessToken());

  subscribeToplan = async (planId: string): Promise<NewSubscriptionResponse> =>
    this.getSubscriptionApi().subscribeToplan(planId, this.getAccessToken());

  cancelSubscription = async (): Promise<any> =>
    this.getSubscriptionApi().cancelSubscription(this.getAccessToken());

  getSubscriptionUsage = async (): Promise<UsageResponse> =>
    this.getSubscriptionApi().getSubscriptionUsage(this.getAccessToken());

  createCustomerPortalSession = async (): Promise<CustomerPortalResponse> =>
    this.getSubscriptionApi().createCustomerPortalSession(this.getAccessToken());

  // Referral endpoints
  getReferralCode = async (): Promise<ReferralCodeResponse> =>
    this.getReferralApi().getReferralCode(this.getAccessToken());

  getReferralStats = async (): Promise<ReferralStatsResponse> =>
    this.getReferralApi().getReferralStats(this.getAccessToken());

  getReferralHistory = async (): Promise<ListResponse<ReferralHistoryItem>> =>
    this.getReferralApi().getReferralHistory(this.getAccessToken());

  // API Keys endpoints
  createApiKey = async (request: ApiKeyCreateRequest): Promise<ApiKeyResponse> =>
    this.getApiKeysApi().createApiKey(request, this.getAccessToken());

  listApiKeys = async (): Promise<ApiKeyResponse[]> =>
    this.getApiKeysApi().listApiKeys(this.getAccessToken());

  updateApiKey = async (id: string, request: ApiKeyUpdateRequest): Promise<{ success: boolean }> =>
    this.getApiKeysApi().updateApiKey(id, request, this.getAccessToken());

  deleteApiKey = async (id: string): Promise<{ success: boolean }> =>
    this.getApiKeysApi().deleteApiKey(id, this.getAccessToken());

  getApiKeyUsage = async (id: string): Promise<ApiKeyUsageResponse> =>
    this.getApiKeysApi().getApiKeyUsage(id, this.getAccessToken());

  // Security log (user-facing audit trail — GET /v1/a/audit)
  getSecurityLog = async (query: SecurityLogQuery = {}): Promise<SecurityLogResponse> =>
    this.getSecurityLogApi().getSecurityLog(query, this.getAccessToken());

  // Connected accounts / identity linking (/v1/a/auth/*)
  listIdentities = async (): Promise<UserIdentitySummary[]> =>
    this.getConnectedAccountsApi().listIdentities(this.getAccessToken());

  linkOidcInitiate = async (): Promise<{ auth_url: string }> =>
    this.getConnectedAccountsApi().linkOidcInitiate(this.getAccessToken());

  reauthInitiate = async (purpose: ReauthPurpose): Promise<StepUpChallenge> =>
    this.getConnectedAccountsApi().reauthInitiate(purpose, this.getAccessToken());

  reauthNostrVerify = async (
    purpose: ReauthPurpose,
    signedEvent: unknown
  ): Promise<{ step_up_token: string }> =>
    this.getConnectedAccountsApi().reauthNostrVerify(purpose, signedEvent, this.getAccessToken());

  linkCommit = async (proofToken: string, stepUpToken: string): Promise<UserIdentitySummary> =>
    this.getConnectedAccountsApi().linkCommit(proofToken, stepUpToken, this.getAccessToken());

  unlinkIdentity = async (id: string, stepUpToken: string): Promise<{ success: true }> =>
    this.getConnectedAccountsApi().unlinkIdentity(id, stepUpToken, this.getAccessToken());

  // Forms endpoints
  newForm = async (data: HttpNewForm): Promise<Form> => {
    const response = await this.client.post<Form>('/a/forms', data, this.getAxiosConfig());
    return response.data;
  };

  updateForm = async (id: string, data: UpdateForm): Promise<void> => {
    await this.client.patch(`/a/forms/${id}`, data, this.getAxiosConfig());
  };

  updateFormSpecs = async (id: string, data: UpdateFormSpecs): Promise<void> => {
    await this.client.patch(`/a/forms/${id}/specs`, data, this.getAxiosConfig());
  };

  deleteForm = async (id: string): Promise<void> => {
    await this.client.delete(`/a/forms/${id}`, this.getAxiosConfig());
  };

  getForms = async (query?: FormsQueryParams): Promise<FormsResponse> => {
    const response = await this.client.get<FormsResponse>(makeUrl('/a/forms', query));
    return response.data;
  };

  getForm = async (id: string): Promise<Form> => {
    const response = await this.client.get<Form>(`/a/forms/${id}`, this.getAxiosConfig());
    return response.data;
  };

  // Email verification endpoints
  newVerifiedEmail = async (data: HttpNewVerifiedEmail): Promise<void> => {
    await this.client.post('/a/emails/verify', data, this.getAxiosConfig());
  };

  verifyVerifiedEmail = async (id: string): Promise<void> => {
    await this.client.post('/a/emails/verify/retry', { verified_email_id: id });
  };

  deleteVerifiedEmail = async (id: string): Promise<void> => {
    await this.client.delete(`/a/emails/${id}`, this.getAxiosConfig());
  };

  setAccountEmail = async (id: string): Promise<void> => {
    await this.client.put(`/a/emails/${id}/set-account-email`, {}, this.getAxiosConfig());
  };

  getVerifiedEmails = async (): Promise<VerifiedEmailsResponse> => {
    const response = await this.client.get<VerifiedEmailsResponse>('/a/emails');
    return response.data;
  };

  // Form recipients endpoints
  newFormRecipient = async (data: NewFormsRecipient): Promise<void> => {
    await this.client.post('/a/forms/recipients', data, this.getAxiosConfig());
  };

  getFormRecipients = async (
    formId: string,
    query?: FormRecipientsQueryParams
  ): Promise<FormsRecipientsResponse> => {
    const response = await this.client.get<FormsRecipientsResponse>(
      makeUrl(`/a/forms/${formId}/recipients`, query)
    );
    return response.data;
  };

  deleteFormRecipient = async (formId: string, recipientId: string): Promise<void> => {
    await this.client.delete(`/a/forms/${formId}/recipients/${recipientId}`, this.getAxiosConfig());
  };

  // Messages endpoints
  getMessage = async (id: string): Promise<SingleMessageResponse> => {
    const response = await this.client.get<SingleMessageResponse>(
      `/a/messages/${id}`,
      this.getAxiosConfig()
    );
    return response.data;
  };

  getMessages = async (query: MessageQueryParams): Promise<MessagesResponse> => {
    const response = await this.client.get<MessagesResponse>(makeUrl('/a/messages', query));
    return response.data;
  };

  updateMessage = async (
    id: string,
    data: HttpUpdateMessage,
    notification?: NotificationConfig | { title: string; message: string }
  ): Promise<void> => {
    const config = this.getAxiosConfig();
    if (notification) {
      (config as any).notification = notification;
    }
    await this.client.patch(`/a/messages/${id}`, data, config);
  };

  deleteMessage = async (id: string): Promise<void> => {
    await this.client.delete(`/a/messages/${id}`, this.getAxiosConfig());
  };

  getMessageView = async (messageId: string): Promise<FormView | null> => {
    const response = await this.client.get<FormView | null>(
      `/a/messages/${messageId}/view`,
      this.getAxiosConfig()
    );
    return response.data;
  };

  getMessageCountByDay = async (params?: {
    form_ids?: string[];
    is_spam?: boolean;
    data_type?: 'views' | 'messages' | 'combined';
  }): Promise<MessageCountByDay[] | ViewCountByDay[] | FormAnalyticsResponse> => {
    const response = await this.client.get(makeUrl('/a/messages/count-by-day', params));
    return response.data;
  };

  getAnalyticsAggregate = async (params?: {
    form_ids?: string[];
    is_spam?: boolean;
  }): Promise<FormAnalyticsAggregateResponse> => {
    const response = await this.client.get<FormAnalyticsAggregateResponse>(
      makeUrl('/a/analytics/aggregate', params)
    );
    return response.data;
  };

  // ---------------------------------------------------------------------
  // Integrations (outbound-integrations engine)
  //
  // Backend: backend/formshive/src/api/integrations.rs
  // The frontend components live in `src/components/Integrations`.
  // ---------------------------------------------------------------------

  newIntegration = async (data: NewIntegrationBody): Promise<IntegrationDto> => {
    const response = await this.client.post<IntegrationDto>('/a/integrations', data);
    return response.data;
  };

  getIntegrations = async (
    query: IntegrationsListQueryParams = {}
  ): Promise<IntegrationsListDto> => {
    const response = await this.client.get<IntegrationsListDto>(makeUrl('/a/integrations', query));
    return response.data;
  };

  getIntegration = async (id: string): Promise<IntegrationDto> => {
    const response = await this.client.get<IntegrationDto>(`/a/integrations/${id}`);
    return response.data;
  };

  updateIntegration = async (id: string, data: UpdateIntegrationBody): Promise<IntegrationDto> => {
    const response = await this.client.patch<IntegrationDto>(
      `/a/integrations/${id}`,
      data,
      this.getAxiosConfig()
    );
    return response.data;
  };

  deleteIntegration = async (id: string): Promise<void> => {
    await this.client.delete(`/a/integrations/${id}`, this.getAxiosConfig());
  };

  rotateIntegrationSecret = async (id: string): Promise<RotateSecretResponse> => {
    const response = await this.client.post<RotateSecretResponse>(
      `/a/integrations/${id}/rotate-secret`,
      {},
      this.getAxiosConfig()
    );
    return response.data;
  };

  testIntegration = async (id: string, eventType?: string): Promise<void> => {
    await this.client.post(
      `/a/integrations/${id}/test`,
      { event_type: eventType },
      this.getAxiosConfig()
    );
  };

  listIntegrationDeliveries = async (
    id: string,
    query: DeliveriesQueryParams = {}
  ): Promise<DeliveriesListDto> => {
    const response = await this.client.get<DeliveriesListDto>(
      makeUrl(`/a/integrations/${id}/deliveries`, query)
    );
    return response.data;
  };

  replayIntegrationDelivery = async (deliveryId: string): Promise<void> => {
    await this.client.post(
      `/a/integrations/deliveries/${deliveryId}/replay`,
      {},
      this.getAxiosConfig()
    );
  };

  // --- Form ⇄ integration links --------------------------------------------

  linkFormIntegration = async (
    formId: string,
    body: LinkFormIntegrationBody
  ): Promise<FormIntegrationDto> => {
    const response = await this.client.post<FormIntegrationDto>(
      `/a/forms/${formId}/integrations`,
      body,
      this.getAxiosConfig()
    );
    return response.data;
  };

  listFormIntegrations = async (formId: string): Promise<FormIntegrationsListDto> => {
    const response = await this.client.get<FormIntegrationsListDto>(
      `/a/forms/${formId}/integrations`
    );
    return response.data;
  };

  updateFormIntegration = async (
    formId: string,
    integrationId: string,
    body: UpdateFormIntegrationBody
  ): Promise<FormIntegrationDto> => {
    const response = await this.client.patch<FormIntegrationDto>(
      `/a/forms/${formId}/integrations/${integrationId}`,
      body,
      this.getAxiosConfig()
    );
    return response.data;
  };

  unlinkFormIntegration = async (formId: string, integrationId: string): Promise<void> => {
    await this.client.delete(
      `/a/forms/${formId}/integrations/${integrationId}`,
      this.getAxiosConfig()
    );
  };

  async downloadFile(fileId: string) {
    const response = await fetch(makeUrl(`${this.baseUrl}/a/files/download/${fileId}`), {
      method: 'GET',
    });
    if (response.ok) {
      const signedUrl = response.url;
      if (signedUrl) {
        return signedUrl;
      }
    }
    throw new Error('File not found');
  }

  // Two-Factor Authentication endpoints
  getTwoFactorStatus = async (): Promise<TwoFactorStatus> => {
    const response = await this.client.get<TwoFactorStatus>('/a/2fa/status');
    return response.data;
  };

  setupTotp = async (name?: string): Promise<TotpSetupResponse> => {
    const response = await this.client.post<TotpSetupResponse>('/a/2fa/setup/totp', {
      name: name || 'Authenticator',
    });
    return response.data;
  };

  verifyTotpSetup = async (setupToken: string, code: string): Promise<TwoFactorSetupComplete> => {
    const response = await this.client.post<TwoFactorSetupComplete>('/a/2fa/setup/totp/verify', {
      setup_token: setupToken,
      code,
    });
    return response.data;
  };

  disableTwoFactorMethod = async (methodId: string, code: string): Promise<void> => {
    await this.client.delete(`/a/2fa/methods/${methodId}`, {
      ...this.getAxiosConfig(),
      data: { code },
    });
  };

  getRecoveryCodesCount = async (): Promise<RecoveryCodesCount> => {
    const response = await this.client.get<RecoveryCodesCount>('/a/2fa/recovery-codes');
    return response.data;
  };

  regenerateRecoveryCodes = async (code: string): Promise<RecoveryCodesResponse> => {
    const response = await this.client.post<RecoveryCodesResponse>(
      '/a/2fa/recovery-codes/regenerate',
      { code }
    );
    return response.data;
  };

  // Issues endpoints
  getIssues = async (params?: IssuesQueryParams): Promise<IssuesResponse> => {
    const response = await this.client.get<IssuesResponse>(makeUrl('/a/issues', params));
    return response.data;
  };

  dismissIssue = async (id: string): Promise<void> => {
    await this.client.patch(`/a/issues/${id}/dismiss`, {}, this.getAxiosConfig());
  };

  // Auto-response test
  sendAutoResponseTest = async (formId: string, email: string): Promise<void> => {
    await this.client.post(
      `/a/forms/${formId}/auto-response/test`,
      { email },
      this.getAxiosConfig()
    );
  };

  // Account data export & deletion
  exportAccountData = async (): Promise<unknown> => {
    const response = await this.client.get('/a/account/export', { timeout: 120000 });
    return response.data;
  };

  deleteAccount = async (
    confirmation: string,
    stepUpToken: string
  ): Promise<{ deleted: boolean }> =>
    this.getConnectedAccountsApi().deleteAccount(confirmation, stepUpToken, this.getAccessToken());

  // Chat assistant (uses native fetch for SSE streaming)
  formChat = async (
    formId: string,
    message: string,
    formState: Record<string, unknown>,
    onChunk: (content: string) => void,
    onDone: () => void,
    onError: (error: string) => void
  ): Promise<void> => {
    const token = this.getAccessToken();
    const url = `${this.baseUrl}/a/forms/${formId}/chat`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message, form_state: formState }),
      });

      if (!response.ok) {
        if (response.status === 429) {
          onError('rateLimit');
          return;
        }
        onError('error');
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        onError('error');
        return;
      }

      const decoder = new TextDecoder();
      // A read boundary can land mid-line, so hold the trailing partial event
      // back until the rest of it arrives — otherwise both halves are dropped.
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data: ')) continue;
          const data = trimmed.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            const content = parsed?.choices?.[0]?.delta?.content;
            if (content) {
              onChunk(content);
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }

      onDone();
    } catch {
      onError('error');
    }
  };
}
